import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, MessageCircle } from 'lucide-react';
import Logo from './Logo';
import { getCategories, getSettings } from '../../api/services';

export default function Footer() {
  // Las categorías salen de la tienda, no hardcodeadas: si no, quedan links a
  // categorías que no existen (llevaban al catálogo completo sin avisar).
  const [categories, setCategories] = useState([]);
  // Los datos de contacto y redes salen de la configuración del admin, no
  // hardcodeados: así el footer siempre refleja lo que está cargado.
  const [settings, setSettings] = useState(null);

  useEffect(() => { getCategories().then(setCategories).catch(() => {}); }, []);
  useEffect(() => { getSettings().then(setSettings).catch(() => {}); }, []);

  // Preferimos las categorías madre, pero si la tienda solo tiene subcategorías
  // igual mostramos esas (si no, la columna quedaría sin links).
  const roots = categories.filter((c) => !c.parent);
  const tiendaLinks = [
    ['Catálogo', '/catalogo'],
    ...(roots.length ? roots : categories).slice(0, 3).map((c) => [c.name, `/categoria/${c.slug}`]),
  ];

  const companyName = settings?.companyName || 'MS Fabrics';
  const tagline = settings?.tagline || '';
  const email = settings?.email || '';
  const phone = settings?.phone || '';
  const social = settings?.social || {};

  // WhatsApp: el link va al teléfono que se muestra en configuración; wa.me
  // necesita solo dígitos, pero se muestra el número tal cual está cargado.
  const waDigits = phone.replace(/\D/g, '');
  // Instagram/Facebook: se muestra el nombre de usuario (último tramo de la URL).
  const igHandle = socialHandle(social.instagram);
  const fbHandle = socialHandle(social.facebook);
  const igUrl = socialUrl(social.instagram, 'https://instagram.com');
  const fbUrl = socialUrl(social.facebook, 'https://facebook.com');

  return (
    <footer className="mt-24 bg-ink text-mist-100">
      <div className="mx-auto max-w-8xl px-5 py-16 sm:px-8 lg:px-12">
        <div className="grid gap-12 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <Logo onDark />
            {tagline && (
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-mist-100/60">{tagline}</p>
            )}
          </div>

          <FooterCol title="Tienda" links={tiendaLinks} />

          <FooterCol title="Ayuda" links={[
            ['Sobre nosotros', '/pagina/nosotros'],
            ['Preguntas frecuentes', '/pagina/preguntas-frecuentes'],
            ['Envíos y devoluciones', '/pagina/envios-devoluciones'],
            ['Términos y condiciones', '/pagina/terminos'],
          ]} />

          <div>
            <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-mist-100/80">Contacto</h4>
            <ul className="space-y-3 text-sm text-mist-100/60">
              {waDigits && (
                <li>
                  <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 transition hover:text-brand-400">
                    <MessageCircle size={15} /> {phone}
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a href={`mailto:${email}`} className="flex items-center gap-2 transition hover:text-brand-400">
                    <Mail size={15} /> {email}
                  </a>
                </li>
              )}
              {igHandle && (
                <li>
                  <a href={igUrl} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 transition hover:text-brand-400">
                    <Instagram size={15} /> @{igHandle}
                  </a>
                </li>
              )}
              {fbHandle && (
                <li>
                  <a href={fbUrl} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 transition hover:text-brand-400">
                    <Facebook size={15} /> {fbHandle}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-mist-100/40 sm:flex-row">
          <p>© {new Date().getFullYear()} {companyName}. Todos los derechos reservados.</p>
          <Link to="/mayorista" className="transition hover:text-brand-400">Venta mayorista</Link>
        </div>
      </div>
    </footer>
  );
}

/** Nombre de usuario visible de una red: el último tramo de la URL guardada. */
function socialHandle(value) {
  if (!value) return '';
  try {
    return new URL(value).pathname.split('/').filter(Boolean).pop() || '';
  } catch {
    // No es una URL: se asume que ya cargaron el usuario suelto.
    return value.replace(/^@/, '').trim();
  }
}

/** URL absoluta de la red, aunque en config solo hayan cargado el usuario. */
function socialUrl(value, base) {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${base}/${value.replace(/^@/, '').trim()}`;
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-mist-100/80">{title}</h4>
      <ul className="space-y-2.5 text-sm text-mist-100/60">
        {links.map(([label, to]) => (
          <li key={label}>
            <Link to={to} className="transition hover:text-brand-400">{label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

